import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiArrowLeft, FiUser } from 'react-icons/fi';
import { adminCustomers, AdminOrderHistoryEntry } from '../../api/admin';
import { formatCurrency } from '../../utils/currency';
import { getStatusLabel } from '../../utils/orderStatus';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #6B5E4E;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 0;

  &:hover {
    color: #1C1511;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  margin-bottom: 4px;
  font-family: Georgia, serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const DetailSection = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const DetailTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #6B5E4E;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: #6B5E4E;
`;

const DetailValue = styled.span`
  color: #1C1511;
  font-weight: 500;
`;

const PointsBalanceSection = styled.div`
  background: #1C1511;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  text-align: center;
`;

const PointsBalanceLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #D4A017;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

const PointsBalanceValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #FFFFFF;
  font-family: Georgia, serif;
`;

const Table = styled.table`
  width: 100%;
  background: #F5F0E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Thead = styled.thead`
  background: #1C1511;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #E8DDD0;

  &:hover {
    background: #ede8df;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1C1511;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
  background: #F5F0E8;
  border-radius: 12px;
`;

const NotFoundState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;

const DiscountInput = styled.input`
  width: 120px;
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 8px;
  font-size: 14px;
  color: #1C1511;
  background: #FFFFFF;
`;

const DiscountSaveButton = styled.button`
  padding: 8px 16px;
  background: #1C1511;
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DiscountControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InlineError = styled.div`
  color: #991b1b;
  font-size: 13px;
  margin-top: 8px;
`;

const InlineSuccess = styled.div`
  color: #166534;
  font-size: 13px;
  margin-top: 8px;
`;

// Up to 2 decimal places, non-negative, no exponent/sign -- mirrors the
// backend's Decimal(max_digits=5, decimal_places=2) 0..100 constraint.
const parseLifetimeDiscount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= 0 && value <= 100 ? value : null;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Type label + order#/amount are read per-variant, branching on
// order_type -- the two order_history variants use different field names
// for id/amount (order_id/total_amount for custom_cakes vs id/total for
// quick_bites), so they must never be normalized/coerced together.
const getOrderTypeLabel = (entry: AdminOrderHistoryEntry, t: (key: string) => string): string =>
  entry.order_type === 'custom_cakes' ? t('admin.customerDetail.customCakesTypeLabel') : t('admin.customerDetail.quickBitesTypeLabel');

const getOrderNumberDisplay = (entry: AdminOrderHistoryEntry): string =>
  entry.order_type === 'custom_cakes' ? `#${entry.order_number}` : `#${entry.id}`;

const getOrderAmount = (entry: AdminOrderHistoryEntry): number =>
  entry.order_type === 'custom_cakes' ? entry.total_amount : entry.total;

const AdminCustomerDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = Number(id);
  const queryClient = useQueryClient();
  const [discountDraft, setDiscountDraft] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountSaved, setDiscountSaved] = useState(false);

  const discountMutation = useMutation({
    mutationFn: (percent: number) => adminCustomers.updateLifetimeDiscount(userId, percent),
    onSuccess: () => {
      setDiscountDraft(null);
      setDiscountError(null);
      setDiscountSaved(true);
      queryClient.invalidateQueries({ queryKey: ['adminCustomerDetail', userId] });
      queryClient.invalidateQueries({ queryKey: ['adminCustomers'] });
    },
    onError: (err: any) => {
      setDiscountSaved(false);
      const detail = err?.response?.data?.detail;
      setDiscountError(typeof detail === 'string' ? detail : t('admin.customerDetail.lifetimeDiscountSaveError'));
    },
  });

  const handleSaveDiscount = () => {
    const parsed = parseLifetimeDiscount(discountDraft ?? String(customer?.lifetime_discount_percent ?? 0));
    setDiscountSaved(false);
    if (parsed === null) {
      setDiscountError(t('admin.customerDetail.lifetimeDiscountInvalid'));
      return;
    }
    setDiscountError(null);
    discountMutation.mutate(parsed);
  };

  const { data: customer, isLoading, isError, error } = useQuery({
    queryKey: ['adminCustomerDetail', userId],
    queryFn: () => adminCustomers.getById(userId),
    enabled: !Number.isNaN(userId),
  });

  const handleBack = () => {
    navigate('/admin/customers');
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (isError) {
    const status = (error as any)?.response?.status;
    if (status === 404) {
      return (
        <Container>
          <BackButton onClick={handleBack}>
            <FiArrowLeft />
            {t('admin.customerDetail.backButton')}
          </BackButton>
          <NotFoundState>
            <h3>{t('admin.customerDetail.notFoundTitle')}</h3>
          </NotFoundState>
        </Container>
      );
    }
    return (
      <Container>
        <BackButton onClick={handleBack}>
          <FiArrowLeft />
          {t('admin.customerDetail.backButton')}
        </BackButton>
        <ErrorMessage>{t('admin.customerDetail.loadError')}</ErrorMessage>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container>
        <BackButton onClick={handleBack}>
          <FiArrowLeft />
          {t('admin.customerDetail.backButton')}
        </BackButton>
        <NotFoundState>
          <h3>{t('admin.customerDetail.notFoundTitle')}</h3>
        </NotFoundState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack}>
          <FiArrowLeft />
          {t('admin.customerDetail.backButton')}
        </BackButton>
        <Title>{customer.first_name} {customer.last_name}</Title>
      </Header>

      <DetailSection>
        <DetailTitle>{t('admin.customerDetail.profileTitle')}</DetailTitle>
        <DetailRow>
          <DetailLabel>{t('admin.customerDetail.nameLabel')}</DetailLabel>
          <DetailValue>{customer.first_name} {customer.last_name}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('admin.customerDetail.emailLabel')}</DetailLabel>
          <DetailValue>{customer.email}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('admin.customerDetail.phoneLabel')}</DetailLabel>
          <DetailValue>{customer.phone ?? '—'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{t('admin.customerDetail.joinedLabel')}</DetailLabel>
          <DetailValue>{formatDate(customer.created_at)}</DetailValue>
        </DetailRow>
      </DetailSection>

      <DetailSection>
        <DetailTitle>
          <label htmlFor="customer-lifetime-discount-input">{t('admin.customerDetail.lifetimeDiscountLabel')}</label>
        </DetailTitle>
        <DiscountControls>
          <DiscountInput
            id="customer-lifetime-discount-input"
            data-testid="customer-lifetime-discount-input"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={discountDraft ?? String(customer.lifetime_discount_percent ?? 0)}
            onChange={(e) => {
              setDiscountDraft(e.target.value);
              setDiscountSaved(false);
            }}
            aria-invalid={discountError ? true : undefined}
            aria-describedby={discountError ? 'customer-lifetime-discount-error' : undefined}
          />
          <DiscountSaveButton
            type="button"
            data-testid="customer-lifetime-discount-save"
            onClick={handleSaveDiscount}
            disabled={discountMutation.isPending}
          >
            {discountMutation.isPending
              ? t('admin.customerDetail.lifetimeDiscountSavingButton')
              : t('admin.customerDetail.lifetimeDiscountSaveButton')}
          </DiscountSaveButton>
        </DiscountControls>
        {discountError && (
          <InlineError id="customer-lifetime-discount-error" data-testid="customer-lifetime-discount-error" role="alert">
            {discountError}
          </InlineError>
        )}
        {discountSaved && !discountError && (
          <InlineSuccess role="status">{t('admin.customerDetail.lifetimeDiscountSaved')}</InlineSuccess>
        )}
      </DetailSection>

      <PointsBalanceSection>
        <PointsBalanceLabel>
          <FiUser style={{ marginRight: 6, verticalAlign: 'middle' }} />
          {t('admin.customerDetail.pointsBalanceLabel')}
        </PointsBalanceLabel>
        <PointsBalanceValue>{customer.loyalty_points_balance}</PointsBalanceValue>
      </PointsBalanceSection>

      <DetailSection>
        <DetailTitle>{t('admin.customerDetail.orderHistoryTitle')}</DetailTitle>
        {customer.order_history.length === 0 ? (
          <EmptyState>
            <h3>{t('admin.customerDetail.noOrdersTitle')}</h3>
            <p>{t('admin.customerDetail.noOrdersDescription')}</p>
          </EmptyState>
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>{t('admin.customerDetail.typeHeader')}</Th>
                <Th>{t('admin.customerDetail.orderNumberHeader')}</Th>
                <Th>{t('admin.customerDetail.dateHeader')}</Th>
                <Th>{t('admin.customerDetail.statusHeader')}</Th>
                <Th>{t('admin.customerDetail.amountHeader')}</Th>
                <Th>{t('admin.customerDetail.pointsEarnedHeader')}</Th>
                <Th>{t('admin.customerDetail.pointsRedeemedHeader')}</Th>
              </tr>
            </Thead>
            <Tbody>
              {customer.order_history.map((entry) => {
                const key = entry.order_type === 'custom_cakes' ? `cc-${entry.order_id}` : `qb-${entry.id}`;
                return (
                  <Tr key={key}>
                    <Td>{getOrderTypeLabel(entry, t)}</Td>
                    <Td>{getOrderNumberDisplay(entry)}</Td>
                    <Td>{formatDate(entry.created_at)}</Td>
                    <Td>{getStatusLabel(entry.status, t)}</Td>
                    <Td>{formatCurrency(getOrderAmount(entry))}</Td>
                    <Td>{entry.points_earned}</Td>
                    <Td>{entry.points_redeemed}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </DetailSection>
    </Container>
  );
};

export default AdminCustomerDetail;
